import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChatCircleText, Sparkle, ArrowsClockwise, User } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminTillyReportPage() {
  const [recent, setRecent] = useState([]);
  const [aiUsage, setAiUsage] = useState(null);
  const [days, setDays] = useState('7');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState(null);

  const getHeaders = useCallback(
    () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }),
    []
  );

  const fetchRecent = useCallback(async () => {
    setLoading(true);
    try {
      const [recentRes, usageRes] = await Promise.all([
        axios.get(`${API}/api/help/admin/chat/recent?days=${days}&limit=200`, { headers: getHeaders() }),
        axios.get(`${API}/api/help/admin/ai-usage?days=${days}`, { headers: getHeaders() }),
      ]);
      setRecent(recentRes.data.items || []);
      setAiUsage(usageRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke hente data');
    } finally {
      setLoading(false);
    }
  }, [days, getHeaders]);

  useEffect(() => {
    fetchRecent();
  }, [fetchRecent]);

  const generateReport = async () => {
    setGenerating(true);
    setReport(null);
    try {
      const { data } = await axios.post(
        `${API}/api/help/admin/chat/report`,
        { days: parseInt(days, 10), max_messages: 200 },
        { headers: getHeaders() }
      );
      setReport(data);
      toast.success(`Rapport generert basert på ${data.message_count} meldinger`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI-rapport feilet');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-tilly-report-page">
      <div>
        <h1 className="font-heading text-4xl tracking-tight font-extrabold">
          Tilly innsikt
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Hva spør brukerne om? Finn smertepunkter og forbedringsområder.
        </p>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium">Tidsrom:</span>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="days-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Siste 24 timer</SelectItem>
              <SelectItem value="7">Siste 7 dager</SelectItem>
              <SelectItem value="30">Siste 30 dager</SelectItem>
              <SelectItem value="90">Siste 90 dager</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={fetchRecent}
            disabled={loading}
            className="gap-2"
            data-testid="refresh-recent-btn"
          >
            <ArrowsClockwise size={14} weight="bold" /> Oppdater liste
          </Button>
          <Button
            onClick={generateReport}
            disabled={generating || loading || recent.length === 0}
            className="gap-2 ml-auto"
            data-testid="generate-report-btn"
          >
            <Sparkle size={14} weight="bold" />
            {generating ? 'Genererer...' : 'Generer AI-rapport'}
          </Button>
        </CardContent>
      </Card>

      {/* AI Usage stats */}
      {aiUsage && (
        <Card data-testid="ai-usage-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Sparkle size={18} weight="bold" />
              AI-belastning siste {aiUsage.days} dager
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <Stat label="Kall totalt" value={aiUsage.overall.calls} />
              <Stat label="Re-tries" value={aiUsage.overall.retries} warn={aiUsage.overall.retries > 0} />
              <Stat label="Feil" value={aiUsage.overall.errors} warn={aiUsage.overall.errors > 0} />
              <Stat label="Input tegn" value={aiUsage.overall.total_input_chars?.toLocaleString('nb-NO')} />
              <Stat label="Output tegn" value={aiUsage.overall.total_output_chars?.toLocaleString('nb-NO')} />
            </div>
            {(aiUsage.by_tenant || []).length > 0 && (
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Per tenant</p>
                {aiUsage.by_tenant.map((t) => (
                  <div key={t.tenant_id || 'unknown'} className="flex items-center justify-between gap-3 p-2 bg-muted/30 rounded text-sm">
                    <span className="font-medium">{t.tenant_id || '— ukjent —'}</span>
                    <div className="flex gap-3 text-xs text-muted-foreground">
                      <span>{t.calls} kall</span>
                      <span className={t.retries > 0 ? 'text-warning' : ''}>{t.retries} retries</span>
                      <span className={t.errors > 0 ? 'text-destructive' : ''}>{t.errors} feil</span>
                      <span>~{t.avg_duration_ms}ms</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {(aiUsage.by_feature || []).length > 0 && (
              <div className="space-y-2 mt-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Per funksjon</p>
                {aiUsage.by_feature.map((f) => (
                  <div key={f.feature} className="flex items-center justify-between gap-3 p-2 bg-muted/20 rounded text-sm">
                    <span className="font-mono text-xs">{f.feature}</span>
                    <div className="flex gap-3 text-xs text-muted-foreground">
                      <span>{f.calls} kall</span>
                      <span className={f.retries > 0 ? 'text-warning' : ''}>{f.retries} retries</span>
                      <span className={f.errors > 0 ? 'text-destructive' : ''}>{f.errors} feil</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {aiUsage.overall.calls === 0 && (
              <p className="text-sm text-muted-foreground py-4 text-center">
                Ingen AI-kall registrert ennå. Telemetri starter med neste AutoGuard-kjøring.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* AI Report */}
      {report && (
        <Card className="border-2 border-primary/30 bg-primary/5" data-testid="ai-report-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Sparkle size={18} weight="bold" className="text-primary" />
              AI-rapport — siste {report.days} dager · {report.message_count} meldinger
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className="prose prose-sm max-w-none whitespace-pre-wrap text-sm"
              data-testid="ai-report-content"
            >
              {report.report}
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Generert {new Date(report.generated_at).toLocaleString('nb-NO')}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Recent messages list */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <ChatCircleText size={18} weight="bold" />
            Siste spørsmål
            <Badge variant="secondary">{recent.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              <div className="h-16 bg-muted animate-pulse rounded" />
              <div className="h-16 bg-muted animate-pulse rounded" />
              <div className="h-16 bg-muted animate-pulse rounded" />
            </div>
          ) : recent.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              Ingen Tilly-meldinger i denne perioden.
            </p>
          ) : (
            <div className="space-y-3">
              {recent.map((m, i) => (
                <div
                  key={`${m.session_id}-${i}`}
                  className="border border-border rounded-md p-3 bg-card"
                  data-testid={`chat-row-${i}`}
                >
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2 flex-wrap">
                    <User size={12} /> <span>{m.user_email || 'ukjent'}</span>
                    {m.page && (
                      <Badge variant="outline" className="text-[10px]">
                        {m.page}
                      </Badge>
                    )}
                    <span className="ml-auto">
                      {new Date(m.created_at).toLocaleString('nb-NO')}
                    </span>
                  </div>
                  <p className="text-sm font-medium mb-2">
                    <span className="text-primary">Spørsmål:</span> {m.question}
                  </p>
                  {m.answer && (
                    <details className="text-xs text-muted-foreground">
                      <summary className="cursor-pointer hover:text-foreground">
                        Tilly svarte ({m.answer.length} tegn)
                      </summary>
                      <p className="mt-2 p-2 bg-muted/40 rounded whitespace-pre-wrap">
                        {m.answer}
                      </p>
                    </details>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, warn }) {
  return (
    <div className="bg-muted/40 rounded p-2">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`font-heading text-lg font-bold ${warn ? 'text-warning' : ''}`}>{value || 0}</p>
    </div>
  );
}
